import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Message } from "../lib/api";


interface RealtimeInboxOptions {
  token: string;
  activeConversationId: string | null;
  tenantId?: string;
}

export function useRealtimeInbox({
  token,
  activeConversationId: _activeConversationId,
  tenantId: _tenantId,
}: RealtimeInboxOptions) {
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!token) return;

    let isUnmounted = false;

    function connect() {
      if (isUnmounted) return;

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const host = window.location.host;
      const wsUrl = `${protocol}//${host}/api/v1/inbox/ws?token=${encodeURIComponent(token)}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (isUnmounted) return;
        setIsConnected(true);
        // Redis Pub/Sub is intentionally non-durable. If a message arrived
        // while the socket was reconnecting (for example during an app
        // restart), reconcile every inbox cache immediately instead of
        // waiting for the periodic polling fallback or a manual refresh.
        queryClient.invalidateQueries({ queryKey: ["inbox", "messages"] });
        queryClient.invalidateQueries({ queryKey: ["inbox", "conversations"] });
      };

      ws.onmessage = (event) => {
        if (isUnmounted) return;
        try {
          const payload = JSON.parse(event.data);
          const { event: eventType, data } = payload;

          if (eventType === "ping") {
            ws.send("pong");
            return;
          }

          if (eventType === "message.new" && data) {
            const { conversation_id, message } = data;

            // 1. Instantly update active thread cache
            queryClient.setQueryData<Message[]>(
              ["inbox", "messages", conversation_id],
              (old) => {
                if (!old) return [message];
                if (old.some((m) => m.id === message.id)) return old;
                return [...old, message];
              }
            );

            // 2. Refresh conversation preview list
            queryClient.invalidateQueries({
              queryKey: ["inbox", "conversations"],
            });
          } else if (eventType === "message.status" && data) {
            const { conversation_id, message_id, provider_message_id, status } = data;

            // Instantly update status of message in cache
            if (conversation_id) {
              queryClient.setQueryData<Message[]>(
                ["inbox", "messages", conversation_id],
                (old) => {
                  if (!old) return old;
                  return old.map((m) => {
                    if (
                      m.id === message_id ||
                      (provider_message_id && m.provider_message_id === provider_message_id)
                    ) {
                      return { ...m, status };
                    }
                    return m;
                  });
                }
              );
            } else {
              queryClient.setQueriesData<Message[]>(
                { queryKey: ["inbox", "messages"] },
                (old) => {
                  if (!old) return old;
                  return old.map((m) => {
                    if (
                      m.id === message_id ||
                      (provider_message_id && m.provider_message_id === provider_message_id)
                    ) {
                      return { ...m, status };
                    }
                    return m;
                  });
                }
              );
            }
          } else if (eventType === "message.deleted" && data) {
            const { conversation_id, message_id } = data;
            if (conversation_id) {
              queryClient.setQueryData<Message[]>(
                ["inbox", "messages", conversation_id],
                (old) => (old ? old.filter((m) => m.id !== message_id) : [])
              );
            }
          } else if (eventType === "message.updated" && data) {
            const { conversation_id, message } = data;
            if (conversation_id && message) {
              queryClient.setQueryData<Message[]>(
                ["inbox", "messages", conversation_id],
                (old) => (old ? old.map((m) => (m.id === message.id ? message : m)) : [message])
              );
            }
          } else if (eventType === "conversation.updated") {
            queryClient.invalidateQueries({
              queryKey: ["inbox", "conversations"],
            });
          } else if (eventType === "instance.status") {
            queryClient.invalidateQueries({
              queryKey: ["qr-instances"],
            });
          }
        } catch {
          // ignore non-json messages
        }
      };

      ws.onclose = () => {
        if (isUnmounted) return;
        setIsConnected(false);
        // Auto-reconnect after 3s
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    return () => {
      isUnmounted = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [token, queryClient]);

  return { isConnected };
}
